import React, {memo, useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useModels1Query} from "../query";
import {getCoreRowModel, useReactTable} from "@tanstack/react-table";
import DataTable from "../../../../components/DataTable";
import StationCode from "../../../../components/columns/StationCode";
import {format, parseISO} from "date-fns";
import useDataTable from "../../../../components/DataTable/useDataTable";
import {formatFilterForAPI} from "../useFeaturesState";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "../../../../materialDashboard/components/MDBox";
import FeatureSelectionChips from "../Panels/FeatureSelectionChips";

const cellWidth = "2rem";

const getHNAux = (row) => {
    return [
        row?.hnR && "R",
        row?.hnT && "T",
        row?.hnW && "W",
        row?.hnMm && "MM",
        row?.hnMe && "ME",
        row?.hnG && "G"
    ].filter(x => x).join(",");
}

const areEqualCellValues = (prevProps, nextProps) => {
    const prevValue = prevProps.cell.getValue();
    const nextValue = nextProps.cell.getValue();
    return prevValue === nextValue;
}

const PeriodCell = memo(({cell}) => {
    const {t} = useTranslation(['enums']);

    const value = cell.getValue();
    if (value === "H24") {
        return <Tooltip title={t('model1Period.H24')} placement="bottom" disableInteractive>
            <span>24h</span>
        </Tooltip>;
    } else if (value === "LT24H") {
        return <Tooltip title={t('model1Period.LT24H')} placement="bottom" disableInteractive>
            <span>{"<24h"}</span>
        </Tooltip>;
    } else if (value === "GT24H") {
        return <Tooltip title={t('model1Period.GT24H')} placement="bottom" disableInteractive>
            <span>{">24h"}</span>
        </Tooltip>;
    } else {
        return "";
    }
}, areEqualCellValues);

const NoteCell = memo(({cell}) => {
    const value = cell.getValue();
    if (value !== "" && value != null) {
        return <Tooltip title={value} placement="bottom" disableInteractive>
            <span>{value.length > 16 ? value.substring(0, 15) + "..." : value}</span>
        </Tooltip>
    } else {
        return null;
    }
}, areEqualCellValues)

const Models1Table = ({layers, featuresState}) => {
    const {t} = useTranslation(["webgis", "enums"]);
    const {pagination, setPagination, sorting, onSortingChange, fetchDataOptions, goToFirstPage} = useDataTable();
    const {model1SelectedState} = featuresState;
    const {selectedFeatures, handleFeatureDeSelect} = model1SelectedState;

    const params = useMemo(() => ({
        ...formatFilterForAPI.model1Stations(layers),
        station__id__in: selectedFeatures.length > 0 ? selectedFeatures.map(f => f.properties.id) : undefined,
        ...fetchDataOptions
    }), [layers, fetchDataOptions, selectedFeatures]);

    const {data, isLoading} = useModels1Query(params);
    const rows = data?.rows ?? [];
    const pageCount = data?.page_count ?? -1;

    useEffect(() => {
        goToFirstPage();
    }, [layers.commonFilters, goToFirstPage]);

    const columns = useMemo(() => [
        {
            accessorKey: 'stationCode',
            cell: props => <StationCode station={props?.row?.original?.station} align="left"/>,
            header: () => <span>{t("models1Table.station")}</span>,
            enableSorting: true,
            meta: { width: "5rem", align: "left" }
        },
        {
            accessorKey: 'datetime',
            cell: props => format(parseISO(props.row?.original?.datetime), "dd/MM/yyyy HH:mm"),
            header: () => <span>{t("models1Table.datetime")}</span>,
            enableSorting: true,
            meta: { width: "12rem", align: "center" }
        },
        {
            accessorKey: 'period',
            cell: props => <PeriodCell cell={props}/>,
            header: () => <span>{t("models1Table.period")}</span>,
            enableSorting: false,
            meta: { width: "3rem", align: "center" }
        },
        ...["ww", "n", "v", "vq1", "vq2", "ta", "tMin", "tMax", "hs", "hn", "hnCum", "hnStim", "ps", "th10", "th30", "pr", "pf", "sf", "sh", "l1", "l2", "l3", "l4", "l5", "l6", "l7", "l8", "l9"].map(param => {
            let headerLabel ;
            if (param === "hnCum") {
                headerLabel = "hnc"
            } else if (param === "hnStim") {
                headerLabel = "hns"
            } else {
                headerLabel = param.replace("_", "");
            }
            return {
                accessorKey: param,
                cell: cell => cell.getValue() ?? "",
                header: () => headerLabel,
                enableSorting: param === "hs" || param === "hn" || param === "l1",
                meta: {
                    align: "center",
                    width: cellWidth,
                    px: "1px",
                    headerTooltip: t(`models1Table.${param}`)
                }
            }
        }),
        {
            accessorKey: 'hnAux',
            cell: cell => getHNAux(cell.row.original),
            header: () => <span>HNAUX</span>,
            enableSorting: false,
            meta: { width: "3rem", align: "center" }
        },
        {
            accessorKey: 'notes',
            cell: props => <NoteCell cell={props}></NoteCell>,
            header: () => <span>{t("models1Table.notes")}</span>,
            enableSorting: false,
            meta: { width: "6rem", align: "left" }
        },
    ], [t]);

    const table = useReactTable({
        data: rows,
        columns,
        pageCount,
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        manualSorting: true,
    });

    if (isLoading) return null;

    return <MDBox>
        <FeatureSelectionChips
            selectedFeatures={selectedFeatures}
            onDeSelectFeature={handleFeatureDeSelect}
            getLabel={(feature) => `${feature.properties.code || feature.properties.id}`}
            sx={{ mb: 2, px: 2 }}
        />
        <DataTable table={table} showTotalEntries={false} />;
    </MDBox>
};

export default Models1Table;
